import { AggregatorSpeciality, OpeningHours as OpeningHoursType, PaymentType } from '@/types-aggregatore';
import { CheckBadgeIcon, CalendarIcon } from '@heroicons/react/24/solid';
import { DeliveryModeTypes } from '@/costants/enum';
import Payments from '@/app/_components/widgets/profile/profileBody/payments';
import DeliveryModes from '@/app/_components/widgets/profile/profileBody/deliveryModes';
import PhoneNumberCTA from '@/app/_components/phoneNumberCTA';
import { ELButton } from '@davincihealthcare/elty-design-system-react';
import { useIsWelfare } from '@/app/_hooks/useIsWelfare';
import { useState } from 'react';
import RecapChangeSpecialization from '@/app/_components/widgets/checkoutWidget/recap/recapChangeSpecialization';
import { useRouter } from 'next/navigation';
import { getCheckoutURL } from '@/utils/checkout';
import { ROUTES } from '@/routes';
import OpeningHours from '@/app/_components/widgets/profile/profileBody/openingHours';
import Section from '@/app/_components/widgets/profile/section';
import { useIsDesktop } from '@/app/_hooks/useIsScreen';
import parse from 'html-react-parser';
import clsx from 'clsx/lite';
type ProfileBodyProps = {
  isLicensed?: boolean;
  name?: string;
  id?: string;
  city?: string;
  address?: string;
  isDomicile: boolean;
  availableDeliveryModes?: DeliveryModeTypes[];
  availablePayments?: PaymentType[];
  description?: string;
  availableOpeningHours?: OpeningHoursType[];
  availableSpecializations: AggregatorSpeciality[];
  questions?: { question: string; className?: string }[];
  setShowMobileMap: (show: boolean) => void;
  setSearchModalVisible: (show: boolean) => void;
};

const AddressSection = ({
  address,
  setShowMobileMap,
  showCTA,
}: {
  address: string;
  setShowMobileMap: (show: boolean) => void;
  showCTA: boolean;
}) => {
  return (
    <div className="flex items-center gap-x-2" data-cy="profile address">
      <h2 className="text-sm font-medium text-neutral-darker">{address}</h2>
      {showCTA && (
        <div className="flex-shrink-0 lg:hidden">
          <ELButton
            onClick={() => setShowMobileMap(true)}
            label="Mostra mappa"
            variant="text"
            color="primary"
            rel="noreferrer"
            size="small"
          />
        </div>
      )}
    </div>
  );
};

const scrollToId = (id: string, offset: number = 0) => {
  const target = document.getElementById(id);
  if (!target) return;
  const headerOffset = target.offsetTop - offset;
  window.scroll({ top: headerOffset, behavior: 'smooth' });
};

const HeaderFaqSection: React.FC<{
  questions: { question: string; className?: string }[];
}> = ({ questions }) => {
  return (
    <Section title="domande frequenti" testId="profile faqs" showDivider={false} className="flex flex-col gap-y-4">
      {questions.map(({ question, className }) => (
        <button
          key={question}
          onClick={() => scrollToId(`profile-faqs-section`, 64)}
          className={clsx('flex text-left text-sm font-semibold text-primary underline', className)}
        >
          {question}
        </button>
      ))}
    </Section>
  );
};

const PrenotaOnlineButton = ({
  setSearchModalVisible,
  disabled,
}: {
  setSearchModalVisible: (show: boolean) => void;
  disabled: boolean;
}) => {
  const isDesktopAndSmaller = useIsDesktop();
  return (
    <ELButton
      label="Prenota online"
      color="secondary"
      leadingIcon={<CalendarIcon />}
      variant="filled"
      size="medium"
      disabled={disabled}
      onClick={() => {
        const input = document.getElementById('autocomplete-centri-medici-input');
        if (!input) return;

        if (isDesktopAndSmaller) {
          setSearchModalVisible(true);
        } else {
          scrollToId('autocomplete-centri-medici-section');
          input.focus();
          input.click();
        }
      }}
    />
  );
};

const phoneNumberCTAAnchorText = 'Oppure chiama Elty al';

const ProfileBody = ({
  isLicensed,
  name,
  city,
  address,
  id,
  availableDeliveryModes,
  availablePayments,
  description,
  availableOpeningHours,
  availableSpecializations,
  questions,
  isDomicile,
  setShowMobileMap,
  setSearchModalVisible,
}: ProfileBodyProps) => {
  const isWelfare = useIsWelfare();
  const [showAllServices, setShowAllServices] = useState(false);
  const router = useRouter();
  const showPaymentsOptions = availablePayments?.length && !isWelfare;
  const isDomiciliary = availableDeliveryModes?.includes(DeliveryModeTypes.Domiciliary) ?? false;
  const [isExpanded, setIsExpanded] = useState(false);
  const isDesktopAndSmaller = useIsDesktop();
  const hasSpecialities = availableSpecializations.length > 0;
  return (
    <>
      {showAllServices && id && (
        <RecapChangeSpecialization
          onNewSlot={selectedSlot => {
            router.push(
              getCheckoutURL({
                pathname: ROUTES.CHECKOUT_RECAP.pathname,
                availabilitySlotId: selectedSlot.id,
              }),
            );
          }}
          onCancel={() => setShowAllServices(false)}
          workgroupId={id}
          workgroupName={name ?? ''}
          domiciliaryCare={isDomiciliary}
          specializations={availableSpecializations}
          isLoading={false}
        />
      )}

      <div className="flex w-full flex-col gap-y-4">
        <div className="inline-flex items-center gap-x-3">
          <h1 className="text-xl font-semibold text-primary lg:text-4xl" data-cy="profile name">
            {name ?? ''}
          </h1>
          {isLicensed && (
            <div>
              <CheckBadgeIcon className="mt-1 size-5 text-primary-active lg:mt-2 lg:size-6" data-cy="profile license" />
            </div>
          )}
        </div>

        {!isDomicile && address && <AddressSection address={address} setShowMobileMap={setShowMobileMap} showCTA={!!isLicensed} />}

        {isLicensed && (
          <div className="flex-start flex">
            <ELButton
              label="Tutte le prestazioni e professionisti sanitari"
              onClick={() => setShowAllServices(true)}
              variant="text"
              color="primary"
              size="small"
            />
          </div>
        )}

        {isLicensed && (
          <div className="hidden lg:inline-flex lg:items-center xl:gap-x-8" data-cy="profile reservation cta">
            <div className="mr-2 whitespace-nowrap">
              <PrenotaOnlineButton setSearchModalVisible={setSearchModalVisible} disabled={!hasSpecialities} />
            </div>
            {!isWelfare && <PhoneNumberCTA anchorText={phoneNumberCTAAnchorText} />}
          </div>
        )}
        {!isLicensed && (
          <h4 className="text-base font-semibold text-neutral-darker lg:text-lg">Al momento non è possibile prenotare online</h4>
        )}
        <h4 className="text-xs font-semibold text-neutral-lighter lg:text-base lg:font-medium">Descrizione</h4>
        <div className={`text-ellipsis text-base font-normal text-neutral-darker [&>ul]:list-disc ${!isExpanded ? 'line-clamp-2' : ''}`}>
          {description && parse(description)}
        </div>

        <div className={`flex flex-col gap-y-4 ${isExpanded ? '' : 'hidden'}`}>
          <hr />
          {isDesktopAndSmaller && !!availableDeliveryModes?.length && (
            <>
              <DeliveryModes deliveryModesList={availableDeliveryModes} city={city} />
              <hr />
            </>
          )}
          {isDesktopAndSmaller && !!showPaymentsOptions && !!availablePayments?.length && (
            <>
              <Payments paymentsList={availablePayments} />
              <hr />
            </>
          )}

          {!!availableOpeningHours?.length && <OpeningHours openingHoursList={availableOpeningHours} />}
          {!!questions?.length && <HeaderFaqSection questions={questions} />}
        </div>

        <div>
          <ELButton
            label={isExpanded ? 'Nascondi info' : 'Leggi di più'}
            color="primary"
            variant="text"
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
          />
        </div>
        {isLicensed && (
          <div className="flex flex-col gap-y-4 lg:hidden">
            <PrenotaOnlineButton setSearchModalVisible={setSearchModalVisible} disabled={!hasSpecialities} />
            {!isWelfare && <PhoneNumberCTA anchorText={phoneNumberCTAAnchorText} className="flex justify-center gap-x-1" />}
          </div>
        )}
      </div>
    </>
  );
};

export default ProfileBody;
